import React from 'react';
import {
    Alert,
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';           // for consents
import SecurityIcon from '@mui/icons-material/Security';   // for insurance

/**
 * Component to display form validation status and errors
 */
const FormValidation = ({
    formik,
    documentValidations,
    electronicSignatureValidation,
    rentPaymentConsent,           // renamed: actually "hasError" = !consent
    utilitiesConsent,
    tenantInsuranceConsent,
}) => {
    // Helper: flatten nested errors object
    const flattenErrors = (obj, prefix = '') => {
        return Object.keys(obj).reduce((acc, key) => {
            const pre = prefix ? `${prefix}.` : '';

            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                Object.assign(acc, flattenErrors(obj[key], `${pre}${key}`));
            } else if (Array.isArray(obj[key])) {
                obj[key].forEach((item, index) => {
                    if (item && typeof item === 'object') {
                        Object.assign(acc, flattenErrors(item, `${pre}${key}[${index}]`));
                    } else if (item) {
                        acc[`${pre}${key}[${index}]`] = item;
                    }
                });
            } else if (obj[key]) {
                acc[`${pre}${key}`] = obj[key];
            }

            return acc;
        }, {});
    };

    // Group errors logically
    const groupErrorsByCategory = (errors) => {
        const flattened = flattenErrors(errors || {});
        const categories = {
            tenants: {
                title: 'Tenant Information',
                icon: <PersonIcon color="error" />,
                errors: [],
            },
            consents: {
                title: 'Required Consents',
                icon: <GavelIcon color="error" />,
                errors: [],
            },
            documents: {
                title: 'Document & Upload Issues',
                icon: <AssignmentIcon color="error" />,
                errors: [],
            },
            other: {
                title: 'Other Issues',
                icon: <InfoIcon color="error" />,
                errors: [],
            },
        };

        // Formik field errors
        Object.entries(flattened).forEach(([key, value]) => {
            if (key.startsWith('tenants')) {
                categories.tenants.errors.push({ key, value });
            } else if (key.startsWith('attached_documents')) {
                categories.documents.errors.push({ key, value });
            } else {
                categories.other.errors.push({ key, value });
            }
        });

        // Consent-specific messages (passed as props — true = problem exists)
        if (rentPaymentConsent) {
            categories.consents.errors.push({
                key: 'rent_payment_consent',
                value: 'You must consent to the rent payment terms (Point #6)',
            });
        }

        if (utilitiesConsent) {
            categories.consents.errors.push({
                key: 'utilities_consent',
                value: 'You must consent to the utilities responsibility/terms  (Point #7)',
            });
        }

        if (tenantInsuranceConsent) {
            categories.consents.errors.push({
                key: 'tenant_insurance_consent',
                value: 'You must confirm tenant/renter’s insurance requirement  (Point #11)',
            });
        }

        // Electronic signature
        if (electronicSignatureValidation) {
            categories.consents.errors.push({
                key: 'electronic_signature_consent',
                value: 'You must consent to electronic signatures to proceed',
            });
        }

        // Document completion
        if (documentValidations) {
            categories.documents.errors.push({
                key: 'document_actions',
                value: 'Please complete/review all required documents and uploads',
            });
        }

        // Only keep categories that actually have errors
        return Object.fromEntries(
            Object.entries(categories).filter(([_, cat]) => cat.errors.length > 0)
        );
    };

    // Human-friendly label for each error key
    const formatErrorKey = (key) => {
        const friendlyNames = {
            rent_payment_consent: 'Rent Payment Consent',
            utilities_consent: 'Utilities Consent',
            tenant_insurance_consent: 'Tenant Insurance Consent',
            electronic_signature_consent: 'Electronic Signature Consent',
            document_actions: 'Document Completion',
        };

        if (friendlyNames[key]) return friendlyNames[key];

        // Fallback for field paths
        const segments = key.split('.');
        let label = segments[segments.length - 1]
            .replace(/\[\d+\]/g, '')
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .trim();

        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    const hasErrors =
        Object.keys(formik.errors || {}).length > 0 ||
        documentValidations ||
        electronicSignatureValidation ||
        rentPaymentConsent ||
        utilitiesConsent ||
        tenantInsuranceConsent;

    const errorGroups = hasErrors ? groupErrorsByCategory(formik.errors) : {};
    const totalErrorCount = Object.values(errorGroups).reduce(
        (sum, group) => sum + group.errors.length,
        0
    );

    return (
        <>
            {hasErrors ? (
                <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{
                        mb: 4,
                        '& .MuiAlert-message': { width: '100%' },
                    }}
                >
                    <Box>
                        <Box display="flex" alignItems="center" mb={1.5}>
                            <Typography variant="h6" component="div">
                                {totalErrorCount} {totalErrorCount === 1 ? 'issue' : 'issues'} need your attention before submitting
                            </Typography>
                        </Box>

                        {Object.values(errorGroups).map((category, idx) => (
                            <Accordion
                                key={idx}
                                disableGutters
                                sx={{
                                    background: 'transparent',
                                    boxShadow: 'none',
                                    '&:before': { display: 'none' },
                                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: 48,
                                        '&.Mui-expanded': { minHeight: 48 },
                                        px: 1,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" width="100%">
                                        {category.icon}
                                        <Typography variant="subtitle1" sx={{ ml: 1.5, fontWeight: 600 }}>
                                            {category.title} ({category.errors.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails sx={{ pt: 0, pb: 1, pl: 5, pr: 2 }}>
                                    <List dense disablePadding>
                                        {category.errors.map((err, i) => (
                                            <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <ErrorIcon fontSize="small" color="error" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={err.value}
                                                    secondary={formatErrorKey(err.key)}
                                                    primaryTypographyProps={{ variant: 'body2', color: 'error.main', fontWeight: 500 }}
                                                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Alert>
            ) : (
                <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 4 }}
                >
                    <Typography variant="body1">
                        All required fields, consents, and documents look good. You can now submit the lease.
                    </Typography>
                </Alert>
            )}
        </>
    );
};

export { FormValidation };